import {
  CheckCircleOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Descriptions,
  Dropdown,
  Empty,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { MenuProps } from 'antd/es/menu';
import { useFetchApiGet, useFetchApiList, usePostApi } from 'hooks/api';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import companyApiCall from 'apps/futureflex/apiCalls/company';
import employeeApiCall from 'apps/futureflex/apiCalls/employee';
import apiFundEcontractsCall from 'apps/investment/apiCalls/fund-econtracts/list';
import listFundAccountApiCall from 'apps/investment/apiCalls/fundAccount/list';
import stockBalanceHistoriesByUserApiCall from 'apps/investment/apiCalls/stock-balance-histories/list-by-user';
import apiCall from 'apps/investment/apiCalls/user';
import { columnsGenerator as columnsGeneratorFundEcontracts } from 'apps/investment/components/fund-econtracts/config';
import { columnsGenerator as columnsGeneratorFundAccount } from 'apps/investment/components/FundAccount/config';
import { identificationStatusColors } from 'apps/investment/constants/user';
import { DataTable } from 'common/data-table';
import OnlineTooltip from 'common/online-tooltip';
import ErrorMessage from 'common/ui/ErrorMessage';
import { formatDateTime } from 'utils/formatDate';
import { columnsStockAccount, columnsStockBalanceHistories } from './config';

const { Item: DescItem } = Descriptions;

const column = {
  xxl: 2,
  xl: 2,
  lg: 2,
  md: 1,
  sm: 1,
  xs: 1,
};

const USER_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Blocked', value: 'blocked' },
  { label: 'Archived', value: 'archived' },
  { label: 'Deleted', value: 'deleted' },
];

const USER_STATUS_COLORS: Record<string, string> = {
  active: 'green',
  blocked: 'red',
  archived: 'orange',
  deleted: 'default',
};

interface ItemDetailProps {
  item: any;
  onRefresh?: () => void;
}

const ItemDetail = ({ item, onRefresh }: ItemDetailProps) => {
  const navigate = useNavigate();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const {
    identification,
    code,
    email,
    id,
    name,
    email_verified,
    ekyc_verified,
    re_kyc,
    phone,
    phone_verified,
    updated_at,
    created_at,
    is_online,
    last_login_at,
    account_type,
    status,
  } = item || {};

  // eKYC approve/reject live on the identification page.
  const {
    post: onSetStatus,
    isLoading: isSettingStatus,
  } = usePostApi(apiCall.setStatus, { onSuccess: () => onRefresh?.() });

  const {
    data: requiredFundAccounts,
    load: loadRequiredFundAccounts,
    isLoading: isLoadingRequiredFundAccounts,
    error: loadRequiredFundAccountsError,
  } = useFetchApiList(employeeApiCall.requiredFundAccounts, {
    resourceName: 'data',
  });

  const {
    data: userEmployees,
    load: loadUserEmployees,
    isLoading: isLoadingUserEmployees,
    error: loadUserEmployeesError,
  } = useFetchApiList(employeeApiCall.list, { resourceName: 'data' });

  const {
    data: userCompanies,
    load: loadUserCompanies,
    isLoading: isLoadingUserCompanies,
    error: loadUserCompaniesError,
  } = useFetchApiList(companyApiCall.list, { resourceName: 'data' });

  const {
    data: stockBalanceHistories,
    isLoading: isLoadingStockBalanceHistories,
    error: errorStockBalanceHistories,
    load: loadStockBalanceHistories,
  } = useFetchApiList(stockBalanceHistoriesByUserApiCall, {
    resourceName: 'data',
  });

  const {
    data: fundAccounts,
    error: errorFundAccounts,
    load: loadFundAccounts,
  } = useFetchApiList(listFundAccountApiCall, {
    resourceName: 'data',
  });

  const {
    data: stockAccount,
    load: loadStockAccount,
    error: errorStockAccount,
  } = useFetchApiGet(apiCall.stockAccount, { resourceName: 'data' });

  const {
    data: fundEcontracts,
    error: errorFundEcontracts,
    load: loadFundEcontracts,
  } = useFetchApiList(apiFundEcontractsCall, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (id) {
      loadStockAccount({ id });
      loadRequiredFundAccounts({ id });
      loadUserEmployees({ user_id: id });
      loadUserCompanies({ user_id: id });
      loadStockBalanceHistories({ user_id: id });
      loadFundAccounts({ user_id: id });
      loadFundEcontracts({ user_id: id });
    }
  }, [id]);

  const { id: ID_id } = identification || {};

  const menuItems: MenuProps['items'] = [
    {
      key: '1',
      label: 'Cập nhật tài khoản',
      onClick: () => navigate(`/investment/users/${item.id}/edit`),
    },
    {
      key: '2',
      label: 'Xem danh tính',
      disabled: !ID_id,
      onClick: () =>
        navigate(`/investment/users/${item.id}/identifications/${ID_id}`),
    },
    {
      key: '2b',
      label: 'Cập nhật danh tính',
      disabled: !ID_id,
      onClick: () =>
        navigate(`/investment/users/${item.id}/identifications/${ID_id}/edit`),
    },
    {
      key: '3',
      label: 'Xem ảnh định danh',
      onClick: () =>
        navigate(`/investment/users/${item.id}/identification-images`),
    },
    {
      key: '5',
      label: 'Xem lịch sử xác thực eKYC',
      onClick: () => navigate(`/investment/users/${id}/user-ekyc-records`),
    },
    {
      key: '6',
      label: 'Xem hợp đồng quỹ mở',
      onClick: () => navigate(`/investment/fund-econtracts?user_id=${id}`),
    },
    {
      key: '7',
      label: 'Xem lịch sử khảo sát',
      onClick: () =>
        navigate(`/investment/user-survey-histories?user_id=${item.id}`),
    },
    {
      key: '9',
      label: 'Xem Danh Sách Mục Tiêu',
      onClick: () => navigate(`/investment/user-programs?user_id=${item.id}`),
    },
  ];

  const handleSetStatus = () => {
    if (!selectedStatus) return;
    onSetStatus(
      { id: String(id), status: selectedStatus },
      {
        onSuccess: () => {
          message.success(`User status updated to "${selectedStatus}"`);
          setStatusModalOpen(false);
        },
        onError: (err: any) => {
          message.error(err?.message || 'Failed to update status');
        },
      }
    );
  };

  return (
    <>
      <Modal
        title="Change User Status"
        open={statusModalOpen}
        onCancel={() => setStatusModalOpen(false)}
        onOk={handleSetStatus}
        okText="Confirm"
        okButtonProps={{
          loading: isSettingStatus,
          danger: selectedStatus !== 'active',
        }}
        destroyOnClose
      >
        <div className="space-y-3 py-2">
          <p className="text-sm text-gray-600">
            Current status:{' '}
            <Tag color={USER_STATUS_COLORS[status] || 'default'}>
              {status || 'active'}
            </Tag>
          </p>
          <Select
            className="w-full"
            placeholder="Select new status"
            options={USER_STATUS_OPTIONS}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
          {selectedStatus === 'blocked' && (
            <p className="text-sm text-red-500">
              Blocking will immediately prevent this user from logging in and
              will force logout any active sessions.
            </p>
          )}
        </div>
      </Modal>
      <div className="space-y-4">
        <div className="flex md:gap-4 gap-2 items-center flex-wrap justify-end w-full">
          <Button
            onClick={() => {
              setSelectedStatus(status || 'active');
              setStatusModalOpen(true);
            }}
            danger={status && status !== 'active'}
          >
            Status:{' '}
            <Tag className="ml-1" color={USER_STATUS_COLORS[status] || 'green'}>
              {status || 'active'}
            </Tag>
          </Button>
          {ID_id ? (
            <Link to={`/investment/users/${item.id}/identifications/${ID_id}`}>
              <Button type={ekyc_verified ? 'default' : 'primary'}>
                View KYC
              </Button>
            </Link>
          ) : null}

          <Link to={`/investment/stock-orders?user_id=${item.id}`}>
            <Button>Xem Lệnh CK</Button>
          </Link>

          <Link to={`/investment/fund-orders?user_id=${item.id}`}>
            <Button>Xem Lệnh Quỹ</Button>
          </Link>
          <Link to={`/investment/users/${item.id}/bank-accounts`}>
            <Button>Xem TK Ngân hàng</Button>
          </Link>
          <Link to={`/robo-advisory/portfolios/${item.id}`}>
            <Button>Xem Danh Mục</Button>
          </Link>
          <div>
            <Dropdown menu={{ items: menuItems }}>
              <Button>
                <EllipsisOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <Card
          size="small"
          title="Tài khoản Stag"
          extra={
            <Link to={`/investment/users/${item.id}/edit`}>
              <Button>Edit</Button>
            </Link>
          }
        >
          <Descriptions column={column} bordered size="small">
            <DescItem label="Tên tài khoản">{name}</DescItem>
            <DescItem label="Email">
              <Space align="center" size="small">
                <OnlineTooltip
                  isOnline={is_online}
                  lastLoginAt={last_login_at}
                />
                {email}
                {email_verified ? <VerifiedTooltip /> : null}
              </Space>
            </DescItem>
            <DescItem label="Trạng thái eKYC">
              {ekyc_verified ? (
                <Tag color="green">Đã xác thực</Tag>
              ) : (
                <Tag color="red">Chưa xác thực</Tag>
              )}
            </DescItem>
            <DescItem label="Số điện thoại">
              <Space align="center">
                {phone}
                {phone_verified ? <VerifiedTooltip /> : null}
              </Space>
            </DescItem>
            <DescItem label="Stag ID">{code}</DescItem>
            <DescItem label="Type">{account_type}</DescItem>
            <DescItem label="Status">
              <Tag color={USER_STATUS_COLORS[status] || 'green'}>
                {status || 'active'}
              </Tag>
            </DescItem>
            <DescItem label="Thời gian tạo">
              {formatDateTime(created_at)}
            </DescItem>
            <DescItem label="Thời gian cập nhật">
              {formatDateTime(updated_at)}
            </DescItem>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title="Xác thực danh tính"
          extra={
            ID_id ? (
              <Link
                to={`/investment/users/${item.id}/identifications/${ID_id}`}
              >
                <Button>Xem chi tiết</Button>
              </Link>
            ) : (
              <Link
                to={`/investment/users/${item.id}/identifications/${account_type}/create`}
              >
                <Button type="primary">Create</Button>
              </Link>
            )
          }
        >
          {identification ? (
            <Space align="center" wrap>
              <Tag
                color={
                  identificationStatusColors[identification.verification_status]
                }
              >
                {identification.verification_status_label ||
                  identification.verification_status}
              </Tag>
              {re_kyc && ekyc_verified ? (
                <Tag color="purple">Chờ duyệt Re-KYC</Tag>
              ) : null}
              <span className="text-gray-500">
                Giấy tờ định danh, ảnh và thông tin ngân hàng được xem ở trang
                chi tiết.
              </span>
            </Space>
          ) : (
            <Empty description="Chưa có thông tin xác thực" />
          )}
        </Card>

        <Card size="small" title="Tài khoản Chứng khoán">
          {!!errorStockAccount ? (
            <ErrorMessage errors={errorStockAccount} />
          ) : (
            <Table
              className="w-full overflow-x-auto"
              dataSource={stockAccount && stockAccount.id ? [stockAccount] : []}
              columns={columnsStockAccount()}
              locale={{
                emptyText: (
                  <Empty description="Chưa yêu cầu mở tài khoản chứng khoán" />
                ),
              }}
              pagination={false}
            />
          )}
        </Card>
        <Card size="small" title="Tài khoản Quỹ">
          {!!errorFundAccounts ? (
            <ErrorMessage errors={errorFundAccounts} />
          ) : (
            <DataTable
              noViewOptions
              columns={columnsGeneratorFundAccount()}
              data={fundAccounts || []}
            />
          )}
        </Card>

        <Card size="small" title="Lịch sử nạp rút">
          {!!errorStockBalanceHistories ? (
            <ErrorMessage errors={errorStockBalanceHistories} />
          ) : (
            <DataTable
              noViewOptions
              containerClassName="max-h-[21vh]"
              columns={columnsStockBalanceHistories()}
              data={stockBalanceHistories || []}
            />
          )}
        </Card>

        <Card size="small" title={'Hợp đồng quỹ mở'}>
          {!!errorFundEcontracts ? (
            <ErrorMessage errors={errorFundEcontracts} />
          ) : (
            <>
              <DataTable
                noViewOptions
                containerClassName="max-h-[21vh]"
                columns={columnsGeneratorFundEcontracts()}
                data={fundEcontracts || []}
              />
              <div className="flex justify-center text-base font-medium py-1">
                <Link to={`/investment/fund-econtracts?user_id=${id}`}>
                  Xem tất cả hđ
                </Link>
              </div>
            </>
          )}
        </Card>

        {item.account_type == 'individual' ? (
          <Card size="small" title="Nhân viên">
            {userEmployees && userEmployees.length > 0 ? (
              <Descriptions column={1} bordered>
                {userEmployees.map(userEmployee => (
                  <DescItem
                    label={
                      userEmployee.full_name
                        ? userEmployee.full_name
                        : userEmployee.email
                    }
                  >
                    <Link to={`/futureflex/employees/${userEmployee.id}`}>
                      Xem chi tiết #{userEmployee.id}
                    </Link>
                  </DescItem>
                ))}
              </Descriptions>
            ) : (
              <Empty description="Chưa tham gia chương trình FutureFlex dành cho nhân viên" />
            )}
          </Card>
        ) : (
          <Card size="small" title="Doanh nghiệp">
            {userCompanies && userCompanies.length > 0 ? (
              <Descriptions column={2} bordered>
                {userCompanies.map(userCompany => (
                  <>
                    <DescItem label={userCompany.name}>
                      <Link to={`/futureflex/companies/${userCompany.id}`}>
                        Xem chi tiết #{userCompany.id}
                      </Link>
                    </DescItem>
                    <DescItem label={'HR Admin'}>
                      <Link
                        to={`/futureflex/staffs?company_id=${userCompany.id}`}
                      >
                        Xem danh sách #{userCompany.id}
                      </Link>
                    </DescItem>
                  </>
                ))}
              </Descriptions>
            ) : (
              <Empty description="Chưa tham gia chương trình FutureFlex dành cho doanh nghiệp" />
            )}
          </Card>
        )}

        {item.account_type == 'individual' &&
        userEmployees &&
        userEmployees.length > 0 ? (
          <Card size="small" title="Tài khoản Quỹ cần mở">
            {requiredFundAccounts && requiredFundAccounts.length > 0 ? (
              <Descriptions column={1} bordered>
                {requiredFundAccounts.map(requiredFundAccount => (
                  <DescItem
                    label={`${
                      requiredFundAccount.fund_manager_name
                    } (${requiredFundAccount.fund_manager.toUpperCase()})`}
                  >
                    {requiredFundAccount.is_approved ? (
                      <Tooltip title="Đã duyệt">
                        <CheckCircleOutlined
                          style={{ color: 'green', fontSize: '20px' }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Chưa tạo hoặc chưa duyệt">
                        <InfoCircleOutlined
                          style={{ color: '#b8860b', fontSize: '20px' }}
                        />
                      </Tooltip>
                    )}
                  </DescItem>
                ))}
              </Descriptions>
            ) : (
              <Empty description="Không có tài khoản quỹ cần mở" />
            )}
          </Card>
        ) : null}
      </div>
    </>
  );
};

export default ItemDetail;

const VerifiedTooltip = () => (
  <Tooltip title="Đã xác thực">
    <CheckCircleOutlined style={{ color: 'green', fontSize: '1.125rem' }} />
  </Tooltip>
);
